import React from 'react';
import { PEOPLE_BY_UID } from './taskModel';
import people from '../../../canon/people.json';
import graceAvatar from '../../../assets/avatars/grace.jpg';
import anthonyAvatar from '../../../assets/avatars/anthony.jpg';
import anneliseAvatar from '../../../assets/avatars/annelise.jpg';

// Portal Work — one Avatar, keyed by assignee uid (tasks carry assigneeUids;
// uid→identity is resolved through the same canon roster taskModel/taskAssignees
// own). Renders the team's real headshot when one exists for that uid, and falls
// back to an initial-in-circle (neutral --text-muted tone, never a per-person
// colour) for anyone without a photo — unassigned, future staff, studio@,
// customers. Used everywhere an avatar renders: list, board, detail, comments,
// the assignee filter.
//
// Size hybrid (Anthony's call, one-line to change — see THRESHOLD): a photo is
// shown at >= 24px (detail header, comments, sub-task rows, filter picker,
// chips-with-name); in the dense in-row / board-card-footer spot (~18-20px) we
// render the initial instead, because a face that small reads as a smudge when
// you're scanning, not identifying. Bump a caller's size to >= 22 to give it the
// photo. No "dim/outline when not-you" treatment — "mine" emphasis is the Mine
// filter's job, not the avatar's.

// Downscaled, face-centered headshots keyed by canon person key. object-position
// is tuned per person so object-fit:cover keeps the face, not a forehead or chin
// (the source crops aren't square). Adjust the percentages here if a face sits
// off-centre — that's the one knob.
const PHOTO_BY_KEY = {
	grace: { src: graceAvatar, position: 'center 32%' },
	anthony: { src: anthonyAvatar, position: 'center 26%' },
	annelise: { src: anneliseAvatar, position: 'center 28%' },
};

// uid -> photo, built from the canon roster so no auth UID is hard-coded here.
const PHOTO_BY_UID = (() => {
	const byUid = {};
	for (const p of people.people || []) {
		if (PHOTO_BY_KEY[p.key]) byUid[p.authUid] = PHOTO_BY_KEY[p.key];
	}
	return byUid;
})();

// teachers-doc id -> auth uid, from the same roster. The schedule, Today and the
// lesson records name a teacher by `teachers` doc id, never by uid (people.json
// keeps the ID spaces apart on purpose), so this is the one bridge from "who is
// teaching" to a face.
const UID_BY_TEACHER_ID = (() => {
	const byTeacher = {};
	for (const p of people.people || []) {
		if (p.teacherId) byTeacher[p.teacherId] = p.authUid;
	}
	return byTeacher;
})();

export function uidForTeacherId(teacherId) {
	return (teacherId && UID_BY_TEACHER_ID[teacherId]) || null;
}

// Photos show at this size and up; below it we render the initial. This single
// number is the whole "faces everywhere vs. initials in dense rows" toggle.
const PHOTO_THRESHOLD = 22;

export function hasPhoto(uid) {
	return !!(uid && PHOTO_BY_UID[uid]);
}

// `decorative`: the person's name is already printed beside the avatar, so the
// image is hidden from screen readers rather than announcing the name twice.
export function Avatar({ uid, size = 24, name, title, className = '', decorative = false }) {
	const person = uid ? PEOPLE_BY_UID[uid] : null;
	const photo = uid ? PHOTO_BY_UID[uid] : null;
	const label = title || person?.name || name || 'Unassigned';
	const dim = { width: size, height: size };

	if (photo && size >= PHOTO_THRESHOLD) {
		return (
			<img
				className={`wt-avatar wt-avatar--photo ${className}`.trim()}
				src={photo.src}
				alt={decorative ? '' : label}
				aria-hidden={decorative || undefined}
				title={label}
				loading="lazy"
				style={{ ...dim, objectPosition: photo.position }}
			/>
		);
	}

	// Initial fallback: known person's canon initials, else the first letter of a
	// supplied name (comment authors), else a neutral user glyph (unassigned).
	const initials =
		person?.initials ||
		(name ? name.trim().charAt(0).toUpperCase() : uid ? '?' : '');
	return (
		<span
			className={`wt-avatar wt-avatar--initial ${className}`.trim()}
			title={label}
			aria-hidden={decorative || undefined}
			style={{ ...dim, fontSize: Math.round(size * (initials.length > 1 ? 0.4 : 0.46)) }}>
			{initials || <i className="fa-sharp fa-solid fa-user" />}
		</span>
	);
}

export default Avatar;
